// src/features/streams/components/StreamMetadataCard.tsx

import { Card, Stack, Group, Text, Badge, ThemeIcon } from '@mantine/core';
import {
  IconCalendar,
  IconBroadcast,
  IconServer,
  IconVideo,
  IconDisc,
} from '@tabler/icons-react';
import { MediaStatusBadge } from '@/features/media/components/MediaStatusBadge';
import { StreamLiveStatusBadge } from './StreamLiveStatusBadge';
import { STREAM_TYPE_LABELS, RECORD_MODE_LABELS } from '@/lib/constants/stream';
import { formatTimestamp } from '@/shared/utils/date';
import type { StreamViewData, StreamLiveStatus } from '@/lib/types/Stream';
import type { StreamType, StreamRecordMode } from '@/lib/constants/stream';
import type { IBackendLiveData } from '@/lib/types/Customer';

interface StreamMetadataCardProps {
  stream: StreamViewData;
  liveStatus: StreamLiveStatus;
  backendMap: Map<number, IBackendLiveData>;
}

export function StreamMetadataCard({ stream, liveStatus, backendMap }: StreamMetadataCardProps) {
  const media = stream.media;
  const streamType = (stream.stream?.core?.streamType ?? 'push') as StreamType;
  const backendId = stream.stream?.core?.backendId ?? 0;
  const backend = backendMap.get(backendId);
  const locationLabel = backend?.locationLabel ?? 'Unknown';
  const recordMode = (stream.stream?.record?.recordMode ?? 'manual') as StreamRecordMode;

  return (
    <Card shadow="sm" withBorder h="100%">
      <Stack gap="md">
        <Group gap="sm">
          <Text size="sm" fw={500} c="dimmed">Status</Text>
          <MediaStatusBadge status={media.mediaStatusType} state={media.mediaStateType} />
        </Group>

        <Group gap="sm">
          <ThemeIcon variant="light" size="sm">
            <IconBroadcast size={14} />
          </ThemeIcon>
          <Text size="sm">Stream Type:</Text>
          <Badge variant="light" size="sm">
            {STREAM_TYPE_LABELS[streamType] ?? streamType}
          </Badge>
        </Group>

        <Group gap="sm">
          <ThemeIcon variant="light" size="sm">
            <IconServer size={14} />
          </ThemeIcon>
          <Text size="sm">Server Location: {locationLabel}</Text>
        </Group>

        <Group gap="sm">
          <ThemeIcon variant="light" size="sm">
            <IconVideo size={14} />
          </ThemeIcon>
          <StreamLiveStatusBadge liveStatus={liveStatus} />
        </Group>

        <Group gap="sm">
          <ThemeIcon variant="light" size="sm">
            <IconDisc size={14} />
          </ThemeIcon>
          <Text size="sm">Recording: {RECORD_MODE_LABELS[recordMode] ?? recordMode}</Text>
        </Group>

        <Group gap="sm">
          <ThemeIcon variant="light" size="sm">
            <IconCalendar size={14} />
          </ThemeIcon>
          <Text size="sm">Created: {formatTimestamp(media.created)}</Text>
        </Group>

        <Group gap="sm">
          <ThemeIcon variant="light" size="sm">
            <IconCalendar size={14} />
          </ThemeIcon>
          <Text size="sm">Updated: {formatTimestamp(media.updated)}</Text>
        </Group>
      </Stack>
    </Card>
  );
}
